import React, { useCallback, useContext, useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_AS_TYPE, INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';
import { useDecision } from '@optimizely/react-sdk';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';
import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { isDesktopDevice } from 'Utilities/devices';
import { INCODE_POST_SALE_SLUG, POST_SALE_DOCS_START_PAGE } from 'Utilities/routing';
import { safeLocalStorage } from 'Utilities/storages';

import { DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY } from '../../../DocumentCapture.helpers';
import { DocumentCaptureOfferContext } from '../../../DocumentCaptureContexts';
import { useIncodeVerificationUrl } from '../../hooks/useIncodeVerificationUrl';
import { useOneTimeToken } from '../../hooks/useOneTimeToken';
import LocalTexts from '../../UploadDocuments.texts.json';

import { UploadDocumentsVerificationWrapper } from './UploadDocumentsVerificationWrapper';

import styles from './UploadDocumentsVerification.scss';

const LocalT = new MDText(LocalTexts.uploadDocumentsVerification);

export const UploadDocumentsVerificationPostSale = () => {
  const { id: dpEnquiryId } = useContext(DocumentCaptureOfferContext) || {};
  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSafeDesktopDevice, setIsSafeDesktopDevice] = useState(false);

  const { token } = useOneTimeToken();

  const authToken = user?.auth_token;

  const onError = useCallback(() => {
    toast({
      content: LocalT.translate('errorContent'),
      heading: LocalT.translate('errorHeading'),
      id: 'incode-verification-error',
      variant: INFOBOX_VARIANT.ERROR,
    });
  }, []);

  const [{ enabled, variationKey }] = useDecision(FLAG.INCODE_SDK_FLOW, {}, {
    overrideUserId: user?.dpSellerId?.toString(),
  });
  const useIncodeSDKFlow = enabled && variationKey === 'on';

  const { incodeUrl, loading } = useIncodeVerificationUrl({
    authToken,
    dpEnquiryId,
    incodeSDKFlow: useIncodeSDKFlow,
    onError,
  });

  useEffect(() => {
    VERIFICATION_ANALYTICS_EVENTS.LOAD_HANDOFF();
    setIsSafeDesktopDevice(isDesktopDevice());
  }, []);

  useEffect(() => {
    const reviewStatusViewVehicles = safeLocalStorage.getItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY) || [];
    const currentEnquiryId = JSON.stringify(dpEnquiryId);

    if (reviewStatusViewVehicles.includes(currentEnquiryId) || !dpEnquiryId) {
      return;
    }

    reviewStatusViewVehicles.push(currentEnquiryId);
    safeLocalStorage.setItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY, reviewStatusViewVehicles);
  }, [dpEnquiryId]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const label = LocalT.translate('continueWithVerification') as string;

  const renderButton = () => {
    const commonProps = {
      fullWidth: true,
      label,
    };

    if (isSafeDesktopDevice && useIncodeSDKFlow) {
      return (
        <Button
          {...commonProps}
          as={BUTTON_AS_TYPE.BUTTON}
          onClick={() => {
            setIsModalOpen(true);
            VERIFICATION_ANALYTICS_EVENTS.INCODE_QR_MODAL_OPEN({
              label,
              url: POST_SALE_DOCS_START_PAGE,
            });
          }}

        />
      );
    }

    return (
      <Button
        {...commonProps}
        as={BUTTON_AS_TYPE.ANCHOR}
        disabled={loading}
        href={useIncodeSDKFlow || !incodeUrl ? `/${vehicle.vrm}${INCODE_POST_SALE_SLUG}` : incodeUrl}
        loading={loading}
        onClick={VERIFICATION_ANALYTICS_EVENTS.CONTINUE_VERIFICATION}
      />
    );
  };

  return (
    <UploadDocumentsVerificationWrapper
      closeModal={closeModal}
      isModalOpen={isModalOpen}
      renderButton={renderButton}
      slug={INCODE_POST_SALE_SLUG}
      token={token}
      vehicle={vehicle}
    >
      <p data-thc-style-override className={styles.info}>{LocalT.translate('secureRedirect')}</p>
    </UploadDocumentsVerificationWrapper>
  );
};
